import { useState, Fragment } from "react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../table"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../dropdown-menu"
import { Button } from "../button"
import { MoreVertical, ChevronRight } from "lucide-react"
import type { TableColumn, Action } from "./CustomTable"

type ExpandableDataTableProps<T> = {
  columns: TableColumn<T>[]
  data: T[]
  actions?: Action<T>[] | ((row: T) => Action<T>[])
  emptyMessage?: React.ReactNode
  renderExpandedRow: (row: T) => React.ReactNode
  onRowClick?: (row: T) => void
  rowClassName?: (row: T) => string
}

export function ExpandableDataTable<T extends { id: string | number }>({
  columns,
  data,
  actions,
  emptyMessage,
  renderExpandedRow,
  onRowClick,
  rowClassName,
}: ExpandableDataTableProps<T>) {
  const [expandedRows, setExpandedRows] = useState<Set<string | number>>(new Set())

  const toggleRow = (id: string | number) => {
    setExpandedRows(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const getRowActions = (row: T): Action<T>[] => {
    if (!actions) return []
    return typeof actions === "function" ? actions(row) : actions
  }

  const hasActions = actions !== undefined
  const totalColumns = 1 + columns.length + (hasActions ? 1 : 0)

  return (
    <div className="rounded-xl bg-card border border-border shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className={`border-b border-border ${onRowClick ? "cursor-pointer" : ""}`}>
              <TableHead className="w-10 bg-muted px-2 py-4" />
              {columns.map(col => (
                <TableHead
                  key={String(col.key)}
                  className={`bg-muted px-6 py-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider ${col.headerClassName || ""}`}
                >
                  {col.label}
                </TableHead>
              ))}
              {hasActions && (
                <TableHead className="text-right bg-muted px-6 py-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Actions
                </TableHead>
              )}
            </TableRow>
          </TableHeader>

          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={totalColumns}
                  className="text-center py-12 text-muted-foreground text-sm"
                >
                  {emptyMessage || "No data available."}
                </TableCell>
              </TableRow>
            ) : (
              data.map(row => {
                const rowActions = getRowActions(row)
                const isExpanded = expandedRows.has(row.id)

                return (
                  <Fragment key={row.id}>
                    <TableRow
                      key={row.id}
                      className={`border-b border-border hover:bg-muted/50 transition-colors duration-150 ${onRowClick ? "cursor-pointer" : ""} ${rowClassName?.(row) ?? ""}`}
                      onClick={() => onRowClick?.(row)}
                    >
                      <TableCell className="px-2 py-4 w-10">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 hover:bg-muted transition-all cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation()
                            toggleRow(row.id)
                          }}
                        >
                          <ChevronRight
                            className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${isExpanded ? "rotate-90" : ""}`}
                          />
                        </Button>
                      </TableCell>

                      {columns.map((col) => (
                        <TableCell
                          key={String(col.key)}
                          className={`px-6 py-4 text-sm text-foreground ${col.cellClassName || ""}`}
                        >
                          {col.render ? col.render(row) : String(row[col.key as keyof T])}
                        </TableCell>
                      ))}

                      {hasActions && rowActions.length > 0 && (
                        <TableCell className="text-right px-6 py-4" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 hover:bg-muted transition-colors cursor-pointer"
                              >
                                <MoreVertical className="h-4 w-4 text-muted-foreground" />
                              </Button>
                            </DropdownMenuTrigger>

                            <DropdownMenuContent
                              align="end"
                              className="bg-card shadow-lg border border-border rounded-lg min-w-40"
                            >
                              {rowActions.map((action, index) => {
                                if (action.hidden && action.hidden(row)) {
                                  return null
                                }

                                const isDisabled =
                                  typeof action.disabled === "function"
                                    ? action.disabled(row)
                                    : action.disabled === true

                                return (
                                  <DropdownMenuItem
                                    key={index}
                                    disabled={isDisabled}
                                    onClick={() => {
                                      if (!isDisabled) {
                                        action.onClick(row)
                                      }
                                    }}
                                    className={`
                                      px-4 py-2.5 text-sm transition-colors
                                      ${isDisabled
                                        ? "text-muted-foreground cursor-not-allowed opacity-60"
                                        : "text-foreground hover:bg-muted cursor-pointer"
                                      }
                                      ${action.className || ""}
                                    `}
                                  >
                                    {action.label}
                                  </DropdownMenuItem>
                                )
                              })}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      )}

                      {hasActions && rowActions.length === 0 && (
                        <TableCell className="text-right px-6 py-4">
                          <span className="text-xs text-muted-foreground">
                            No actions
                          </span>
                        </TableCell>
                      )}
                    </TableRow>

                    {isExpanded && (
                      <TableRow className="bg-primary/5">
                        <TableCell colSpan={totalColumns} className="px-0 py-0">
                          <div className="border-l-4 border-l-primary px-6 py-5">
                            {renderExpandedRow(row)}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                )
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
